import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, apiErr } from "@/lib/api";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  BarChart3, Plus, Loader2, Trash2, Copy, Archive, Send, Pencil, Users, GripVertical,
  Play, XCircle, RotateCcw, FileSpreadsheet, FileText, ChevronRight, Sparkles, LayoutTemplate,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from "recharts";

const CHART_COLORS = ["#0b2a6b", "#D4AF37", "#2563eb", "#059669", "#dc2626", "#7c3aed", "#0891b2", "#ea580c"];
const QTYPES = [
  { v: "single", l: "Single choice" },
  { v: "multiple", l: "Multiple choice" },
  { v: "yesno", l: "Yes / No" },
  { v: "rating", l: "Rating (1–5)" },
  { v: "text", l: "Text response" },
];
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600",
  scheduled: "bg-violet-100 text-violet-700",
  active: "bg-emerald-100 text-emerald-700",
  closed: "bg-rose-100 text-rose-700",
  archived: "bg-amber-100 text-amber-700",
};

const uid = () => Math.random().toString(36).slice(2, 10);
function emptyQuestion(type = "single") {
  return {
    id: uid(), text: "", type,
    options: type === "single" || type === "multiple" ? [{ id: uid(), label: "" }, { id: uid(), label: "" }] : [],
    allow_other: false, rating_max: 5, required: true,
  };
}
function emptyPoll() {
  return {
    title: "", description: "", category: "General",
    questions: [emptyQuestion("single")],
    settings: { anonymous: false, allow_change_vote: true, result_visibility: "after_close" },
    audience: { type: "school", class_names: [], sections: [] },
    schedule: { start_at: null, end_at: null },
  };
}

export default function PollsManage() {
  const { user } = useAuth();
  const readOnly = user?.role === "super_admin";
  const [meta, setMeta] = useState(null);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [analyticsId, setAnalyticsId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {id, action, title}

  const load = async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        api.get("/polls/meta"),
        api.get("/polls"),
      ]);
      setMeta(m.data);
      setPolls(p.data.polls || []);
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(emptyPoll()); setFormOpen(true); };
  const openTemplates = async () => {
    try {
      const r = await api.get("/polls/templates");
      setTemplates(r.data.templates || []);
      setTplOpen(true);
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
  };
  const applyTemplate = (tpl) => {
    const p = emptyPoll();
    p.title = tpl.name;
    p.description = tpl.description;
    p.category = tpl.category || "General";
    p.questions = (tpl.questions || []).map((q) => ({
      id: uid(), text: q.text, type: q.type,
      options: (q.options || []).map((o) => ({ id: uid(), label: o.label })),
      allow_other: !!q.allow_other, rating_max: q.rating_max || 5,
      required: q.required !== false,
    }));
    setEditing(p);
    setTplOpen(false);
    setFormOpen(true);
  };
  const openEdit = async (poll) => {
    try {
      const r = await api.get(`/polls/${poll.id}`);
      const d = r.data.poll;
      setEditing({
        id: d.id, title: d.title, description: d.description || "", category: d.category || "General",
        questions: (d.questions || []).map((q) => ({ ...q, options: q.options || [] })),
        settings: d.settings, audience: d.audience, schedule: d.schedule || { start_at: null, end_at: null },
        response_count: d.response_count,
      });
      setFormOpen(true);
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
  };

  const doAction = async (id, action) => {
    const LABELS = { publish: "published", close: "closed", reopen: "reopened", archive: "archived", "open-now": "opened" };
    try {
      await api.post(`/polls/${id}/${action}`);
      toast.success(`Poll ${LABELS[action] || action}`);
      load();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
  };
  const doDelete = async (id) => {
    try { await api.delete(`/polls/${id}`); toast.success("Poll deleted"); load(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
  };
  const doDuplicate = async (id) => {
    try { await api.post(`/polls/${id}/duplicate`); toast.success("Poll duplicated"); load(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (tab === "all") return polls;
    return polls.filter((p) => p.effective_status === tab);
  }, [polls, tab]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="polls-manage-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-[#0b2a6b] dark:text-amber-400" /> Polls & Surveys
            </h1>
            <p className="text-sm text-slate-500 mt-1">Create polls for your students, track participation and view live results.</p>
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={openTemplates} data-testid="polls-templates-btn">
                <LayoutTemplate className="h-4 w-4 mr-1.5" /> Templates
              </Button>
              <Button onClick={openCreate} className="bg-[#0b2a6b] hover:bg-[#153e94]" data-testid="polls-create-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Create Poll
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-1.5 flex-wrap" data-testid="polls-status-tabs">
          {["all", "draft", "scheduled", "active", "closed", "archived"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`polls-tab-${t}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition ${tab === t ? "bg-[#0b2a6b] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="polls-empty">
            <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No polls here yet.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((p) => <PollRow key={p.id} poll={p} readOnly={readOnly}
              onEdit={() => openEdit(p)} onAnalytics={() => setAnalyticsId(p.id)}
              onAction={doAction} onDuplicate={() => doDuplicate(p.id)}
              onConfirm={(action, title) => setConfirm({ id: p.id, action, title })} />)}
          </div>
        )}
      </div>

      {formOpen && (
        <PollForm meta={meta} initial={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }} />
      )}

      {tplOpen && (
        <Dialog open onOpenChange={setTplOpen}>
          <DialogContent className="max-w-2xl" data-testid="polls-templates-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Start from a template</DialogTitle>
              <DialogDescription>Pick a ready-made survey. You can fully customise it before publishing.</DialogDescription>
            </DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {templates.map((t) => (
                <button key={t.id} onClick={() => applyTemplate(t)} data-testid={`poll-template-${t.id}`}
                  className="text-left p-4 rounded-xl border border-slate-200 hover:border-[#0b2a6b] hover:shadow-md transition">
                  <div className="font-semibold text-slate-800">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</div>
                  <div className="text-[11px] text-[#0b2a6b] mt-2 font-medium">{t.questions.length} question(s) · {t.category}</div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {analyticsId && (
        <AnalyticsDialog pollId={analyticsId} onClose={() => setAnalyticsId(null)} />
      )}

      {confirm && (
        <AlertDialog open onOpenChange={() => setConfirm(null)}>
          <AlertDialogContent data-testid="polls-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm.action === "delete" ? "Delete poll?" : `${confirm.action} poll?`}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirm.action === "delete"
                  ? `“${confirm.title}” will be permanently removed. This cannot be undone.`
                  : `Confirm you want to ${confirm.action} “${confirm.title}”.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="polls-confirm-action"
                className={confirm.action === "delete" ? "bg-rose-600 hover:bg-rose-700" : ""}
                onClick={() => { const c = confirm; setConfirm(null); c.action === "delete" ? doDelete(c.id) : doAction(c.id, c.action); }}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Layout>
  );
}

function PollRow({ poll, readOnly, onEdit, onAnalytics, onAction, onDuplicate, onConfirm }) {
  const s = poll.effective_status;
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-wrap items-center gap-4" data-testid={`poll-row-${poll.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800 dark:text-white">{poll.title}</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLE[s] || "bg-slate-100"}`} data-testid={`poll-status-${poll.id}`}>{s}</span>
        </div>
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {poll.audience_label}</span>
          <span>{poll.response_count || 0} responses</span>
          <span>by {poll.created_by?.name}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button size="sm" variant="outline" onClick={onAnalytics} data-testid={`poll-analytics-${poll.id}`}>
          <BarChart3 className="h-3.5 w-3.5 mr-1" /> Analytics
        </Button>
        {!readOnly && <>
          {s === "draft" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onConfirm("publish", poll.title)} data-testid={`poll-publish-${poll.id}`}><Send className="h-3.5 w-3.5 mr-1" /> Publish</Button>}
          {s === "scheduled" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onAction(poll.id, "open-now")} data-testid={`poll-opennow-${poll.id}`}><Play className="h-3.5 w-3.5 mr-1" /> Open now</Button>}
          {(s === "draft" || s === "scheduled") && <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`poll-edit-${poll.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
          {(s === "active" || s === "scheduled") && <Button size="sm" variant="outline" onClick={() => onConfirm("close", poll.title)} data-testid={`poll-close-${poll.id}`}><XCircle className="h-3.5 w-3.5 mr-1" /> Close</Button>}
          {s === "closed" && <Button size="sm" variant="outline" onClick={() => onConfirm("reopen", poll.title)} data-testid={`poll-reopen-${poll.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen</Button>}
          <Button size="sm" variant="ghost" onClick={onDuplicate} data-testid={`poll-duplicate-${poll.id}`}><Copy className="h-3.5 w-3.5" /></Button>
          {s !== "archived" && <Button size="sm" variant="ghost" onClick={() => onConfirm("archive", poll.title)} data-testid={`poll-archive-${poll.id}`}><Archive className="h-3.5 w-3.5" /></Button>}
          <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-600" onClick={() => onConfirm("delete", poll.title)} data-testid={`poll-delete-${poll.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </>}
      </div>
    </div>
  );
}

// ───────── Poll create/edit form ─────────
function PollForm({ meta, initial, onClose, onSaved }) {
  const [poll, setPoll] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [eligible, setEligible] = useState(null);
  const hasResponses = (initial?.response_count || 0) > 0;
  const set = (patch) => setPoll((p) => ({ ...p, ...patch }));
  const setAud = (patch) => setPoll((p) => ({ ...p, audience: { ...p.audience, ...patch } }));
  const setSet = (patch) => setPoll((p) => ({ ...p, settings: { ...p.settings, ...patch } }));

  useEffect(() => {
    let ok = true;
    api.post("/polls/preview-audience", { audience: poll.audience })
      .then((r) => { if (ok) setEligible(r.data.eligible); })
      .catch(() => { if (ok) setEligible(null); });
    return () => { ok = false; };
  }, [poll.audience]);

  const updateQ = (qid, patch) => set({ questions: poll.questions.map((q) => q.id === qid ? { ...q, ...patch } : q) });
  const addQ = () => set({ questions: [...poll.questions, emptyQuestion("single")] });
  const removeQ = (qid) => set({ questions: poll.questions.filter((q) => q.id !== qid) });
  const changeType = (qid, type) => {
    const opts = (type === "single" || type === "multiple") ? [{ id: uid(), label: "" }, { id: uid(), label: "" }] : [];
    updateQ(qid, { type, options: opts, allow_other: false });
  };
  const addOpt = (qid) => { const q = poll.questions.find((x) => x.id === qid); updateQ(qid, { options: [...q.options, { id: uid(), label: "" }] }); };
  const removeOpt = (qid, oid) => { const q = poll.questions.find((x) => x.id === qid); updateQ(qid, { options: q.options.filter((o) => o.id !== oid) }); };
  const setOpt = (qid, oid, label) => { const q = poll.questions.find((x) => x.id === qid); updateQ(qid, { options: q.options.map((o) => o.id === oid ? { ...o, label } : o) }); };

  const toIso = (local) => local ? new Date(local).toISOString() : null;
  const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); const off = d.getTimezoneOffset(); return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16); };

  const save = async (publish) => {
    if (!poll.title.trim()) return toast.error("Please add a poll title.");
    if (!poll.questions.length) return toast.error("Add at least one question.");
    setSaving(true);
    const payload = {
      title: poll.title, description: poll.description, category: poll.category,
      questions: poll.questions.map((q) => ({
        id: q.id, text: q.text, type: q.type,
        options: q.options.map((o) => ({ id: o.id, label: o.label })),
        allow_other: q.allow_other, rating_max: Number(q.rating_max) || 5, required: q.required,
      })),
      settings: poll.settings, audience: poll.audience, schedule: poll.schedule,
    };
    try {
      if (poll.id) {
        await api.put(`/polls/${poll.id}`, payload);
        if (publish) await api.post(`/polls/${poll.id}/publish`);
      } else {
        await api.post(`/polls?publish=${publish}`, payload);
      }
      toast.success(publish ? "Poll published" : "Poll saved");
      onSaved();
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
    setSaving(false);
  };

  const classes = meta?.classes || [];
  const sections = meta?.sections || [];
  const toggleClass = (cn) => {
    const has = poll.audience.class_names.includes(cn);
    setAud({ class_names: has ? poll.audience.class_names.filter((c) => c !== cn) : [...poll.audience.class_names, cn] });
  };
  const toggleSection = (cn, sec) => {
    const key = (s) => `${s.class_name}-${s.section}`;
    const has = poll.audience.sections.some((s) => key(s) === `${cn}-${sec}`);
    setAud({ sections: has ? poll.audience.sections.filter((s) => key(s) !== `${cn}-${sec}`) : [...poll.audience.sections, { class_name: cn, section: sec }] });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="poll-form-dialog">
        <DialogHeader>
          <DialogTitle>{poll.id ? "Edit poll" : "Create poll"}</DialogTitle>
          <DialogDescription>Build your survey, choose who receives it and configure how results appear.</DialogDescription>
        </DialogHeader>

        {hasResponses && (
          <div className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg p-2.5">
            This poll already has responses — you can update settings & schedule, but questions are locked. Duplicate it to make a new version.
          </div>
        )}

        <div className="space-y-5">
          {/* Basic */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wide">Basic information</h3>
            <div>
              <Label>Poll title</Label>
              <Input value={poll.title} onChange={(e) => set({ title: e.target.value })} placeholder="Foreign Language Preference Survey" data-testid="poll-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={poll.description} onChange={(e) => set({ description: e.target.value })} rows={2} placeholder="Tell students why you're running this poll." data-testid="poll-desc-input" />
            </div>
          </section>

          {/* Questions */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wide">Questions</h3>
              {!hasResponses && <Button size="sm" variant="outline" onClick={addQ} data-testid="poll-add-question"><Plus className="h-3.5 w-3.5 mr-1" /> Add question</Button>}
            </div>
            {poll.questions.map((q, qi) => (
              <div key={q.id} className="rounded-xl border border-slate-200 p-3 space-y-3 bg-slate-50/60" data-testid={`poll-question-${qi}`}>
                <div className="flex items-start gap-2">
                  <span className="mt-2 text-xs font-bold text-slate-400">Q{qi + 1}</span>
                  <Input value={q.text} onChange={(e) => updateQ(q.id, { text: e.target.value })} placeholder="Type your question" disabled={hasResponses} data-testid={`poll-q-text-${qi}`} />
                  <div className="w-40 shrink-0">
                    <Select value={q.type} onValueChange={(v) => changeType(q.id, v)} disabled={hasResponses}>
                      <SelectTrigger data-testid={`poll-q-type-${qi}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{QTYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {!hasResponses && poll.questions.length > 1 && (
                    <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => removeQ(q.id)} data-testid={`poll-q-remove-${qi}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>

                {(q.type === "single" || q.type === "multiple") && (
                  <div className="pl-6 space-y-2">
                    {q.options.map((o, oi) => (
                      <div key={o.id} className="flex items-center gap-2">
                        <GripVertical className="h-3.5 w-3.5 text-slate-300" />
                        <Input value={o.label} onChange={(e) => setOpt(q.id, o.id, e.target.value)} placeholder={`Option ${oi + 1}`} disabled={hasResponses} data-testid={`poll-q-${qi}-opt-${oi}`} />
                        {!hasResponses && q.options.length > 2 && <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => removeOpt(q.id, o.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    ))}
                    {!hasResponses && <Button size="sm" variant="ghost" className="text-[#0b2a6b]" onClick={() => addOpt(q.id)} data-testid={`poll-q-${qi}-add-opt`}><Plus className="h-3.5 w-3.5 mr-1" /> Add option</Button>}
                    <div className="flex items-center gap-2 pt-1">
                      <Switch checked={q.allow_other} onCheckedChange={(v) => updateQ(q.id, { allow_other: v })} disabled={hasResponses} data-testid={`poll-q-${qi}-other`} />
                      <span className="text-xs text-slate-500">Allow “Other — please specify”</span>
                    </div>
                  </div>
                )}
                {q.type === "rating" && (
                  <div className="pl-6 flex items-center gap-2">
                    <span className="text-xs text-slate-500">Scale 1 to</span>
                    <Input type="number" min={2} max={10} value={q.rating_max} onChange={(e) => updateQ(q.id, { rating_max: e.target.value })} className="w-20" disabled={hasResponses} />
                  </div>
                )}
                <div className="pl-6 flex items-center gap-2">
                  <Switch checked={q.required} onCheckedChange={(v) => updateQ(q.id, { required: v })} disabled={hasResponses} />
                  <span className="text-xs text-slate-500">Required</span>
                </div>
              </div>
            ))}
          </section>

          {/* Audience */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wide">Who should receive this poll?</h3>
            <div className="flex gap-2 flex-wrap">
              {meta?.can_school_wide && (
                <AudBtn active={poll.audience.type === "school"} onClick={() => setAud({ type: "school", class_names: [], sections: [] })} testid="poll-aud-school">Whole school</AudBtn>
              )}
              <AudBtn active={poll.audience.type === "classes"} onClick={() => setAud({ type: "classes", sections: [] })} testid="poll-aud-classes">Selected standards</AudBtn>
              <AudBtn active={poll.audience.type === "sections"} onClick={() => setAud({ type: "sections", class_names: [] })} testid="poll-aud-sections">Selected sections</AudBtn>
            </div>
            {poll.audience.type === "classes" && (
              <div className="flex flex-wrap gap-2">
                {classes.map((cn) => (
                  <label key={cn} className={`px-3 py-1.5 rounded-lg border text-sm cursor-pointer flex items-center gap-2 ${poll.audience.class_names.includes(cn) ? "border-[#0b2a6b] bg-[#0b2a6b]/5" : "border-slate-200"}`}>
                    <Checkbox checked={poll.audience.class_names.includes(cn)} onCheckedChange={() => toggleClass(cn)} data-testid={`poll-class-${cn}`} />
                    <span>Class {cn}</span>
                  </label>
                ))}
              </div>
            )}
            {poll.audience.type === "sections" && (
              <div className="flex flex-wrap gap-2">
                {sections.map((s) => {
                  const on = poll.audience.sections.some((x) => x.class_name === s.class_name && x.section === s.section);
                  return (
                    <label key={`${s.class_name}-${s.section}`} className={`px-3 py-1.5 rounded-lg border text-sm cursor-pointer flex items-center gap-2 ${on ? "border-[#0b2a6b] bg-[#0b2a6b]/5" : "border-slate-200"}`}>
                      <Checkbox checked={on} onCheckedChange={() => toggleSection(s.class_name, s.section)} data-testid={`poll-section-${s.class_name}-${s.section}`} />
                      <span>{s.class_name}-{s.section}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="text-sm font-medium text-[#0b2a6b] flex items-center gap-1.5" data-testid="poll-eligible-count">
              <Users className="h-4 w-4" /> Eligible students: {eligible === null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : eligible}
            </div>
          </section>

          {/* Settings */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wide">Settings</h3>
            <ToggleRow label="Anonymous voting" hint="Hide who voted for what — only aggregate results are visible."
              checked={poll.settings.anonymous} onChange={(v) => setSet({ anonymous: v })} testid="poll-anon" />
            <ToggleRow label="Allow students to change their vote" hint="Students can update their answer until the poll closes."
              checked={poll.settings.allow_change_vote} onChange={(v) => setSet({ allow_change_vote: v })} testid="poll-change" />
            <div>
              <Label>Result visibility</Label>
              <Select value={poll.settings.result_visibility} onValueChange={(v) => setSet({ result_visibility: v })}>
                <SelectTrigger data-testid="poll-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.result_visibility_options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* Schedule */}
          <section className="space-y-3">
            <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wide">Schedule (optional)</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Start</Label>
                <Input type="datetime-local" value={toLocal(poll.schedule.start_at)} onChange={(e) => set({ schedule: { ...poll.schedule, start_at: toIso(e.target.value) } })} data-testid="poll-start" />
              </div>
              <div>
                <Label>End (auto-closes)</Label>
                <Input type="datetime-local" value={toLocal(poll.schedule.end_at)} onChange={(e) => set({ schedule: { ...poll.schedule, end_at: toIso(e.target.value) } })} data-testid="poll-end" />
              </div>
            </div>
            <p className="text-xs text-slate-400">Leave Start empty to open immediately on publish. Leave End empty to keep it open until you close it.</p>
          </section>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="poll-save-draft">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
          </Button>
          <Button className="bg-[#0b2a6b] hover:bg-[#153e94]" onClick={() => save(true)} disabled={saving} data-testid="poll-publish-btn">
            <Send className="h-4 w-4 mr-1.5" /> {poll.id ? "Save & publish" : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AudBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${active ? "border-[#0b2a6b] bg-[#0b2a6b] text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
      {children}
    </button>
  );
}
function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div>
        <div className="text-sm font-medium text-slate-700">{label}</div>
        <div className="text-xs text-slate-400">{hint}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

// ───────── Analytics ─────────
function AnalyticsDialog({ pollId, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("results");
  useEffect(() => {
    api.get(`/polls/${pollId}/analytics`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e?.response?.data?.detail)));
  }, [pollId]);

  const download = (fmt, studentWise = false) => {
    const token = localStorage.getItem("sa_token");
    const url = `${API_BASE}/polls/${pollId}/export?format=${fmt}${studentWise ? "&student_wise=true" : ""}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `poll.${fmt}`;
        a.click();
      }).catch(() => toast.error("Download failed"));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="poll-analytics-dialog">
        {!data ? (
          <>
            <DialogHeader className="sr-only"><DialogTitle>Poll analytics</DialogTitle>
              <DialogDescription>Loading poll analytics</DialogDescription></DialogHeader>
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{data.poll.title}</DialogTitle>
              <DialogDescription>{data.poll.audience_label} · {data.anonymous ? "Anonymous" : "Identified"} poll</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Eligible" value={data.participation.eligible} />
              <Stat label="Responses" value={data.participation.voted} tone="emerald" />
              <Stat label="Pending" value={data.participation.pending} tone="amber" />
              <Stat label="Participation" value={`${data.participation.rate}%`} tone="navy" />
            </div>

            <div className="flex gap-1.5 mt-2">
              {["results", "class-wise", !data.anonymous && "students"].filter(Boolean).map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`analytics-tab-${t}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize ${tab === t ? "bg-[#0b2a6b] text-white" : "bg-slate-100 text-slate-600"}`}>{t}</button>
              ))}
            </div>

            {tab === "results" && (
              <div className="space-y-6 mt-2">
                {data.questions.map((q) => <QuestionResult key={q.question_id} q={q} />)}
              </div>
            )}
            {tab === "class-wise" && (
              <div className="space-y-6 mt-2">
                {data.questions.filter((q) => q.classwise).map((q) => (
                  <div key={q.question_id}>
                    <div className="font-semibold text-sm text-slate-700 mb-2">{q.text}</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead><tr className="bg-slate-50">
                          <th className="text-left p-2 border">Option</th>
                          {q.classwise.classes.map((c) => <th key={c} className="p-2 border text-center">{c}</th>)}
                        </tr></thead>
                        <tbody>
                          {q.classwise.rows.map((r) => (
                            <tr key={r.option_id}>
                              <td className="p-2 border font-medium">{r.label}</td>
                              {r.by_class.map((b) => <td key={b.class_name} className="p-2 border text-center">{b.count}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                {!data.questions.some((q) => q.classwise) && <p className="text-sm text-slate-400">No choice questions to break down.</p>}
              </div>
            )}
            {tab === "students" && !data.anonymous && (
              <div className="grid sm:grid-cols-2 gap-4 mt-2">
                <RosterList title={`Voted (${data.voted_students?.length || 0})`} rows={data.voted_students} tone="emerald" />
                <RosterList title={`Pending (${data.pending_students?.length || 0})`} rows={data.pending_students} tone="amber" />
              </div>
            )}

            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => download("xlsx")} data-testid="poll-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
              {!data.anonymous && <Button variant="outline" onClick={() => download("xlsx", true)} data-testid="poll-export-xlsx-students"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel + students</Button>}
              <Button variant="outline" onClick={() => download("pdf")} data-testid="poll-export-pdf"><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function QuestionResult({ q }) {
  if (q.type === "text") {
    return (
      <div>
        <div className="font-semibold text-sm text-slate-700 mb-2">{q.text}</div>
        {q.texts?.length ? (
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {q.texts.map((t, i) => <div key={i} className="text-xs bg-slate-50 rounded-lg p-2 text-slate-600">{t}</div>)}
          </div>
        ) : <p className="text-xs text-slate-400">No responses yet.</p>}
      </div>
    );
  }
  if (q.type === "rating") {
    const chart = Object.entries(q.distribution || {}).map(([k, v]) => ({ name: k, value: v }));
    return (
      <div>
        <div className="font-semibold text-sm text-slate-700 mb-1">{q.text}</div>
        <div className="text-xs text-slate-500 mb-2">Average: <span className="font-bold text-[#0b2a6b]">{q.average}</span> / {q.rating_max}</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chart}><XAxis dataKey="name" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>{chart.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
  // choice
  const chart = (q.options || []).map((o) => ({ name: o.label, value: o.count, pct: o.percentage }));
  return (
    <div>
      <div className="font-semibold text-sm text-slate-700 mb-2">{q.text} <span className="text-xs font-normal text-slate-400">({q.answered} answered)</span></div>
      <div className="grid sm:grid-cols-2 gap-3 items-center">
        <div className="space-y-2">
          {chart.map((o, i) => (
            <div key={i}>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-slate-600">{o.name}</span><span className="font-semibold">{o.value} · {o.pct}%</span></div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${o.pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
              </div>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={chart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.pct}%`}>
              {chart.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Legend wrapperStyle={{ fontSize: 10 }} /><Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const map = { emerald: "text-emerald-600", amber: "text-amber-600", navy: "text-[#0b2a6b]" };
  return (
    <div className="rounded-xl border border-slate-200 p-3 text-center bg-white">
      <div className={`text-2xl font-bold ${map[tone] || "text-slate-800"}`}>{value}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
function RosterList({ title, rows, tone }) {
  return (
    <div>
      <div className={`text-xs font-bold uppercase mb-2 ${tone === "emerald" ? "text-emerald-600" : "text-amber-600"}`}>{title}</div>
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {(rows || []).map((s) => (
          <div key={s.id} className="text-xs flex justify-between bg-slate-50 rounded p-1.5">
            <span>{s.name}</span><span className="text-slate-400">{s.class_name}-{s.section}</span>
          </div>
        ))}
        {!(rows || []).length && <p className="text-xs text-slate-400">None.</p>}
      </div>
    </div>
  );
}
